import { useState, type ReactNode } from 'react';
import Expander from './Expander';
import Icon from './Icon';

/**
 * A section that opens and closes smoothly.
 *
 * WHY NOT `<details>`
 *
 * Four of these were plain `<details>` elements, which snap. There is no way to
 * animate one: the browser sets the content to `display: none` when it closes,
 * and nothing transitions out of `display: none`. The newer `::details-content`
 * would fix it and is not yet in enough browsers to rely on.
 *
 * So the same shape is rebuilt from a button and `Expander`, which measures the
 * real height and animates to it — see that file for the two approaches that
 * were tried first and do not work.
 *
 * The `open` prop is a STARTING state, not a controlled one, which is how the
 * `<details>` it replaces actually behaved: React writes the attribute once and
 * does not re-render when somebody clicks the summary.
 */
export default function Reveal({
  summary,
  open: initial = false,
  children,
}: {
  summary: ReactNode;
  open?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(initial);
  return (
    <div className="reveal">
      <button
        type="button"
        className="reveal-summary"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {/* The icon set's chevron, not a text triangle. A "▸" is drawn at
            whatever weight the font feels like — here it came out thin and easy
            to miss, which was the whole complaint. This is a stroked path at a
            weight we choose, and it turns rather than swapping character. */}
        <span className={`reveal-caret ${open ? 'is-open' : ''}`} aria-hidden="true">
          <Icon name="chevron" size={18} />
        </span>
        {summary}
      </button>
      <Expander open={open}>{children}</Expander>
    </div>
  );
}
